// API配置和基础请求函数
const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: {
    code: string
    message: string
  }
  message?: string
  pagination?: {
    page: number
    limit: number
    total: number
    hasMore: boolean
  }
}

// 获取认证信息
const getAuthData = () => {
  const raw = localStorage.getItem('talknect-teacher-auth')
  if (raw) {
    try {
      const obj = JSON.parse(raw)
      return {
        token: obj?.token || obj?.state?.token || null,
        refreshToken: obj?.refreshToken || obj?.state?.refreshToken || null
      }
    } catch {
      return { token: null, refreshToken: null }
    }
  }
  return { token: null, refreshToken: null }
}

// 获取认证头
const getAuthHeaders = () => {
  const { token } = getAuthData()

  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  }
}

// 处理认证失效，强制退出登录
const handleAuthExpired = () => {
  // 清除本地存储的认证信息
  localStorage.removeItem('talknect-teacher-auth')

  // 重定向到登录页面
  const currentPath = window.location.pathname
  // 避免在登录页面重复跳转
  if (!currentPath.includes('/login') && !currentPath.includes('/register')) {
    window.location.href = '/login?expired=true'
  }
}

// 保存新的认证信息
const saveAuthData = (token: string, refreshToken?: string) => {
  const existing = localStorage.getItem('talknect-teacher-auth')
  let authData = existing ? JSON.parse(existing) : { state: {} }
  if (!authData.state) authData.state = {}
  authData.state.token = token
  if (refreshToken) {
    authData.state.refreshToken = refreshToken
  }
  localStorage.setItem('talknect-teacher-auth', JSON.stringify(authData))
}

// 检查并刷新令牌（在操作过程中延续登录时间）
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

const tryRefreshToken = async (): Promise<boolean> => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  const { refreshToken } = getAuthData()
  if (!refreshToken) {
    return false
  }

  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.data?.token) {
          saveAuthData(data.data.token, data.data.refreshToken)

          return true
        }
      }
      return false
    } catch (error) {
      console.error('刷新令牌失败:', error)
      return false
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// 在成功请求后检查是否需要刷新令牌（基于请求频率）
let lastRefreshTime = 0
const REFRESH_INTERVAL = 30 * 60 * 1000 // 每30分钟检查一次是否需要刷新

const checkAndRefreshToken = async () => {
  const now = Date.now()
  if (now - lastRefreshTime > REFRESH_INTERVAL) {
    const { refreshToken } = getAuthData()
    if (refreshToken) {
      lastRefreshTime = now
      // 异步刷新，不阻塞当前请求
      tryRefreshToken().catch(() => { })
    }
  }
}

// 通用请求函数
export const api = {
  async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      // 检查是否是 FormData，如果是则不设置 Content-Type，让浏览器自动处理
      const isFormData = options.body instanceof FormData

      const headers: Record<string, string> = {
        ...getAuthHeaders(),
        ...options.headers as Record<string, string>
      }

      // FormData 上传时删除 Content-Type，让浏览器自动设置 multipart/form-data 和 boundary
      if (isFormData) {
        delete headers['Content-Type']
      }

      const response = await fetch(`${API_BASE_URL}/api/v1${endpoint}`, {
        ...options,
        headers
      })

      // 检测认证失效（401 Unauthorized 或 403 Forbidden）
      if (response.status === 401 || response.status === 403) {
        // 尝试刷新令牌
        const refreshed = await tryRefreshToken()
        if (refreshed) {
          // 刷新成功，重试原请求
          const retryHeaders: Record<string, string> = {
            ...getAuthHeaders(),
            ...options.headers as Record<string, string>
          }
          // FormData 上传时删除 Content-Type
          if (isFormData) {
            delete retryHeaders['Content-Type']
          }
          const retryResponse = await fetch(`${API_BASE_URL}/api/v1${endpoint}`, {
            ...options,
            headers: retryHeaders
          })
          if (retryResponse.ok) {
            const retryData = await retryResponse.json()
            return retryData
          }
        }
        // 刷新失败或重试失败，跳转到登录页
        console.warn('认证已失效，正在退出登录...')
        handleAuthExpired()
        throw new Error('您已长时间未操作，请重新登录')
      }

      const data = await response.json()

      if (!response.ok) {
        // 显示完整的响应对象
        console.error('API错误详情:', data)
        console.error('API错误详情(JSON):', JSON.stringify(data, null, 2))
        console.error('error对象:', data.error)
        console.error('error.debug:', data.error?.debug)
        console.error('error.message:', data.error?.message)
        const debugInfo = data.error?.debug ? ` [DEBUG: ${data.error.debug}]` : ''
        throw new Error(data.error?.message || `操作失败，请稍后重试${debugInfo}`)
      }

      // 请求成功，检查是否需要刷新令牌（延续登录时间）
      checkAndRefreshToken()

      return data
    } catch (error) {
      console.error('API请求失败:', error)

      // 处理不同类型的错误
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        throw new Error('网络连接失败，请检查网络连接或联系技术支持')
      }

      if (error instanceof TypeError && error.message.includes('CORS')) {
        throw new Error('跨域请求被阻止，请联系技术支持')
      }

      // 如果是已知的Error对象，直接抛出
      if (error instanceof Error) {
        throw error
      }

      // 其他未知错误
      throw new Error('未知错误，请重试')
    }
  },

  async get<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' })
  },

  async post<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    })
  },

  async put<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    })
  },

  async patch<T = any>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    })
  },

  async delete<T = any>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  },

  setToken(token: string) {
    const existing = localStorage.getItem('talknect-teacher-auth')
    const authData = existing ? JSON.parse(existing) : { state: {} }
    authData.state.token = token
    localStorage.setItem('talknect-teacher-auth', JSON.stringify(authData))
  },

  removeToken() {
    const existing = localStorage.getItem('talknect-teacher-auth')
    if (existing) {
      const authData = JSON.parse(existing)
      authData.state.token = null
      localStorage.setItem('talknect-teacher-auth', JSON.stringify(authData))
    }
  }
}

// 教材分类相关API
export async function getMaterialCategories(flat?: boolean): Promise<ApiResponse<any[]>> {
  const endpoint = `/admin/categories${flat ? '?flat=1' : ''}`
  return api.get<any[]>(endpoint)
}

export async function getCategoryMaterials(
  categoryId: number,
  params?: {
    include_children?: boolean  // 修复：与后端API参数名保持一致
    page?: number
    limit?: number
    is_active?: number
  }
): Promise<ApiResponse<{ materials: any[]; pagination: any }>> {
  const queryParams: string[] = []
  if (params?.include_children) queryParams.push('include_children=1')
  if (params?.page) queryParams.push(`page=${params.page}`)
  if (params?.limit) queryParams.push(`limit=${params.limit}`)
  if (params?.is_active !== undefined) queryParams.push(`is_active=${params.is_active}`)
  const queryString = queryParams.join('&')
  const endpoint = `/admin/categories/${categoryId}/materials${queryString ? `?${queryString}` : ''}`
  return api.get<{ materials: any[]; pagination: any }>(endpoint)
}
